import { useEffect } from 'react';
import { useFilePicker } from 'use-file-picker';
import { t } from 'i18next';
import PulsingGradientCircle from './PulsingGradientCircle';
import { Button } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { ReactComponent as ProfileFillIconSvg } from '../../../icons/profile.fill.svg';
import type { BettieSettingStep } from '../BettieSettingPage';

interface ProfileImageSelectProps {
  bettieName: string;
  onImageSelected: (image: string) => void;
  setPreviewObjectUrl: (url: string | null) => void;
  setCurrentStep: (step: BettieSettingStep) => void;
  setStepHistory: React.Dispatch<React.SetStateAction<BettieSettingStep[]>>;
}

const ProfileImageSelect = ({
  bettieName,
  onImageSelected,
  setPreviewObjectUrl,
  setCurrentStep,
  setStepHistory
}: ProfileImageSelectProps) => {
  const { openFilePicker, plainFiles, filesContent, errors } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  });

  useEffect(() => {
    if (!plainFiles?.[0] || !filesContent?.[0]) {
      // plainFiles와 filesContent가 순차적으로 업데이트되어 이 이펙트가 두 번 실행될 수 있어
      // 둘 다 준비되었을 때만 처리하여 중복 실행을 방지
      return;
    }
    const file = plainFiles[0];

    // 5MB 초과 파일은 경고 후 진행 중단
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      alert(t('BettieSetting.profileImage.sizeLimitAlert'));
      setPreviewObjectUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewObjectUrl(objectUrl);
    onImageSelected(filesContent[0].content);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [plainFiles, filesContent, onImageSelected, setPreviewObjectUrl]);

  useEffect(() => {
    if (errors.length > 0) {
      console.error('파일 선택 오류:', errors);
    }
  }, [errors]);

  const handleOpenFilePicker = () => {
    openFilePicker();
  };

  const handleBackClick = () => {
    setCurrentStep('loading');
    setStepHistory((prev) => prev.slice(0, -2));
  };

  return (
    <div className="relative w-full h-full flex flex-col">
      <header className="fixed top-[env(safe-area-inset-top)] left-0 right-0 z-50 px-[16px] py-[16px]">
        <ArrowLeft size={24} className="text-main-dark-1" onClick={handleBackClick} />
      </header>
      <div className="flex-1 flex flex-col items-center justify-center px-[24px] pt-[80px] pb-[200px]">
        <div className="flex flex-col items-center gap-[14px] mb-[43px]">
          <h1 className="text-pure-white text-center text-[26px] font-bold leading-[31px] whitespace-pre-line">
            {t('BettieSetting.profileImage.title')}
          </h1>
          <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px]">
            {t('BettieSetting.profileImage.description')}
          </p>
        </div>
        <div className="flex flex-col items-center gap-[20px]">
          <PulsingGradientCircle
            gradientCircleSize="140px"
            glassCircleSize="140px"
            showDottedCircle={false}
            blur="11px"
            isOnlyDark
          >
            <div
              className="bg-theirty-light rounded-full flex items-center justify-center"
              style={{
                width: '138px',
                height: '138px',
                padding: '5px'
              }}
            >
              <ProfileFillIconSvg className="fill-white" width="97px" height="97px" />
            </div>
          </PulsingGradientCircle>
          <p className="text-pure-white text-[16px] font-semibold leading-[19px]">{bettieName}</p>
        </div>
      </div>
      <div className="fixed left-0 right-0 bg-pure-black px-[24px] bottom-0 pb-[calc(env(safe-area-inset-bottom)+10px)]">
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={handleOpenFilePicker}
        >
          {t('BettieSetting.profileImage.openAlbumButton')}
        </Button>
      </div>
    </div>
  );
};

export default ProfileImageSelect;
